import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { BlobServiceClient } from "@azure/storage-blob";
import { useAzureSas } from "./useAzureSas";
import { useUploadStore } from "./useUploadStore";
import { useDeleteCollectEvent } from "./useDeleteCollectEvent";

interface PostParams {
  id: string;
  collectEventId: string;
  enterprise: string;
  organization: string;
  file: File;
  userId: string;
  email: string;
  collectEventName?: string;
  overwrite?: boolean;
}

function formatElapsedTime(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function useOrthoUploader({
  org,
  mutationOptions,
}: {
  org?: string;
  mutationOptions?: UseMutationOptions<
    { elapsedTime: string; response: unknown } | undefined,
    Error,
    PostParams,
    unknown
  >;
}) {
  const { fetchSas } = useAzureSas(org);
  const deleteCollectEventMutation = useDeleteCollectEvent(org);

  const store = useUploadStore.getState();
  const blockSize = 4 * 1024 * 1024; // 4MB blocks
  const concurrency = 4;

  async function uploadLargeFile(data: PostParams) {
    const { id, file, ...metadata } = data;
    const stringMetadata = Object.entries(metadata).reduce<
      Record<string, string>
    >((acc, [k, v]) => {
      acc[k] = String(v);
      return acc;
    }, {});

    const newFile = new File([file], `${metadata.collectEventId}.zip`, {
      type: file.type,
    });
    // Validate ZIP file
    const validMimeTypes = ["application/zip", "application/x-zip-compressed"];
    const validExtension = file.name.toLowerCase().endsWith(".zip");
    if (!validMimeTypes.includes(file.type) || !validExtension) {
      throw new Error("File must be a valid ZIP file");
    }

    const abortController = new AbortController();
    const startDate = Date.now();

    store.setUpload(id, {
      collectEventName: metadata.collectEventName,
      filename: file.name,
      fileSize: file.size,
      abortController,
      progress: 0,
      elapsedTime: "0m 0s",
      isError: false,
      statusMessage: null,
    });

    // Independent timer to update elapsed time every second
    // This provides user feedback even if onProgress doesn't fire
    const timerId = setInterval(() => {
      store.setUpload(id, {
        elapsedTime: formatElapsedTime(Date.now() - startDate),
      });
    }, 1000);

    // Create blob client with fresh SAS token
    const getClient = async () => {
      try {
        const sasData = await fetchSas();
        if (!sasData?.container || !sasData?.service || !sasData?.sas) {
          throw new Error("Invalid SAS token data");
        }
        const { container, service, sas } = sasData;
        const blobServiceClient = new BlobServiceClient(`${service}?${sas}`);
        const client = blobServiceClient
          .getContainerClient(container)
          .getBlockBlobClient(newFile.name);
        // clear uncommited blocks
        await client.commitBlockList([]);
        return client;
      } catch (err: unknown) {
        clearInterval(timerId);
        store.updateStatus(id, {
          isError: true,
          statusMessage: `Failed to fetch SAS token: ${err instanceof Error ? err.message : String(err)}`,
        });
        throw err;
      }
    };

    const blobClient = await getClient();

    try {
      const uploadOptions = {
        blockSize,
        maxSingleShotSize: blockSize, // Force block upload for files > 4MB to enable incremental progress
        metadata: stringMetadata,
        abortSignal: abortController.signal,
        concurrency,
        onProgress: (progress: { loadedBytes: number }) => {
          const percentComplete = (progress.loadedBytes / file.size) * 100;
          store.setUpload(id, {
            progress: Number(percentComplete.toFixed(2)),
          });
        },
      };

      const res = await blobClient.uploadData(newFile, uploadOptions);
      clearInterval(timerId);
      const elapsedTime = formatElapsedTime(Date.now() - startDate);
      store.setUpload(id, { elapsedTime });
      return {
        response: res,
        elapsedTime,
      };
    } catch (err: unknown) {
      clearInterval(timerId);
      const errorMessage = err instanceof Error ? err.message : String(err);
      const statusMessage = abortController.signal.aborted
        ? "Cancelled"
        : errorMessage;
      store.updateStatus(id, {
        isError: true,
        statusMessage,
      });
      if (abortController.signal.aborted) {
        try {
          await blobClient.commitBlockList([], {
            abortSignal: abortController.signal,
          });
          console.log("Cleaned up uncommitted blocks on abort");
        } catch (cleanupErr: unknown) {
          const cleanupErrorMessage =
            cleanupErr instanceof Error
              ? cleanupErr.message
              : String(cleanupErr);
          console.warn(
            "Failed to clean up uncommitted blocks:",
            cleanupErrorMessage
          );
        }
      }
      if (!abortController.signal.aborted) {
        throw new Error(errorMessage);
      }
    }
  }

  const mutation = useMutation({
    mutationFn: uploadLargeFile,
    onSettled: (res, err, variables) => {
      // error on upload, delete collect event
      if (!variables.overwrite && (err || !res) && variables.collectEventId) {
        deleteCollectEventMutation.mutate(variables.collectEventId);
      }
    },
    ...mutationOptions,
  });

  return mutation;
}
